import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';

import { ApiError } from '@shared/api/envelope';
import { staleTimes } from '@shared/api/query-keys';
import { ThemeProvider } from '@shared/theme/ThemeProvider';
import { AnnouncerProvider, ToastProvider } from '@shared/ui';

import { router } from './router';

/**
 * TanStack Query is the ONLY data layer in this repo — one cache, one
 * invalidation story, across every route.
 *
 * The retry policy here is deliberately narrow: `@shared/api/client.ts`
 * already retries a GET twice on a network error / 502 / 504 and never retries
 * a mutation, so Query itself must not retry on top of that. A retried
 * money-moving mutation is a second payment.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: staleTimes.list,
      gcTime: 5 * 60_000,
      retry: false,
      refetchOnWindowFocus: false,
      throwOnError: (error) => error instanceof ApiError && error.status >= 500,
    },
    mutations: {
      retry: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <AnnouncerProvider>
            <RouterProvider router={router} />
          </AnnouncerProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
